import moment from "moment"
import * as React from "react"
import styled, { css } from "styled-components"

import ActionPairButton from "~/components/molecules/ActionPairButton"
import {
  Color,
  IsMobile,
  LineHeight,
  RadiusSize,
  Shadow,
  SpacingSize,
  TextSize,
  Transition,
} from "~/consts/style-mixins"

interface Props {
  open: boolean
  selectedScheduleValues: string[]
  onSelectSchedule: Function
  mainAction: Function
  subAction: Function
  isLoading?: boolean
  availableLateNight?: boolean
  excludeDateStart?: string
  excludeDateEnd?: string
}

interface StyledProps {
  open?: boolean
  selected?: boolean
}

interface scheduleItem {
  label: string
  value: string
  checked?: boolean
}

const CourseSchduleSelectModal = ({
  open,
  selectedScheduleValues,
  onSelectSchedule,
  mainAction,
  subAction,
  isLoading,
  availableLateNight,
  excludeDateStart,
  excludeDateEnd,
}: Props) => {
  const generateDates = (): scheduleItem[] => {
    moment.locale("ja", {
      weekdaysShort: ["日", "月", "火", "水", "木", "金", "土"],
    })

    let dates = [...Array(7)]
    dates = dates.map((_, i) => {
      // NOTE: 翌々日以降の日時を表示
      return moment().add(i + 2, "days")
    })

    let result: scheduleItem[] = []

    if (excludeDateStart && excludeDateEnd) {
      dates = dates.filter((date) => !date.isBetween(excludeDateStart, excludeDateEnd))
    }

    dates.map((date) => {
      // if (date.day() !== 0) {
      const data = [
        {
          label: `${date.format("M月DD日（ddd）")} 10:00〜13:00`,
          value: `${date.format("YYYY-MM-DD")} 10:00:00`,
        },
        {
          label: `${date.format("M月DD日（ddd）")} 13:00〜16:00`,
          value: `${date.format("YYYY-MM-DD")} 13:00:00`,
        },
        {
          label: `${date.format("M月DD日（ddd）")} 16:00〜19:00`,
          value: `${date.format("YYYY-MM-DD")} 16:00:00`,
        },
      ]
      result.push(...data)

      if (availableLateNight) {
        result.push(
          ...[
            {
              label: `${date.format("MM月DD日（dd）")} 19:00〜22:00`,
              value: `${date.format("YYYY-MM-DD")} 19:00:00`,
            },
          ]
        )
      }
      // }
    })
    return result
  }

  const submitButtonText = (): string => {
    if (selectedScheduleValues.length < 2) {
      return "2つ以上の日時を選択してください"
    } else if (selectedScheduleValues.length > 5) {
      return "選択できる日時は最大5つまでです"
    } else {
      return "この日時で説明会に申し込む"
    }
  }

  const enableToSubmit = (): boolean => {
    return selectedScheduleValues.length <= 5 && selectedScheduleValues.length >= 2
  }

  return (
    <>
      <StyledDialogWrapper open={open}>
        <StyledDialogHeading>
          説明会に参加できる日時を<span className="strong">最低2つ</span>選択してください
        </StyledDialogHeading>
        <StyledDialogContent>
          <StyledDescription>
            説明会はオンラインで30分程になります。
            <br />
            選択して頂いた時間の中で実施する日時を決めさせて頂きます。
          </StyledDescription>
          <StyledSchduleWrapper>
            {generateDates().map((scheduleItem: scheduleItem, i) => {
              return (
                <Label className="checkbox" key={i}>
                  <input
                    type="checkbox"
                    name="schedule"
                    value={scheduleItem.value}
                    className="checkbox-input"
                    onChange={() => {
                      if (selectedScheduleValues.includes(scheduleItem.value)) {
                        const newSelectedScheduleValues = selectedScheduleValues.filter(
                          (value) => scheduleItem.value !== value
                        )
                        onSelectSchedule([...newSelectedScheduleValues])
                      } else {
                        onSelectSchedule([...selectedScheduleValues, scheduleItem.value])
                      }
                    }}
                  />
                  <div className="checkbox-content">
                    <span className="checkbox-content-parts" />
                    <span className="checkbox-content-text">{scheduleItem.label}</span>
                  </div>
                </Label>
              )
            })}
          </StyledSchduleWrapper>
        </StyledDialogContent>
        <StyledActionPairButtonWrapper>
          <ActionPairButton
            mainAction={{
              label: submitButtonText(),
              action: enableToSubmit() ? mainAction : () => {},
              isLoading: isLoading,
              disabled: !enableToSubmit(),
              className: enableToSubmit() ? "gtm_course-complete-consultation" : "",
            }}
            subAction={{
              label: "キャンセル",
              action: subAction,
            }}
            vertical
          />
        </StyledActionPairButtonWrapper>
      </StyledDialogWrapper>
      <StyledDialogOverlay open={open} />
    </>
  )
}

const StyledDialogWrapper = styled.div`
  position: relative;
  width: calc(100% - 32px);
  max-width: 400px;
  max-height: calc(100vh - 60px);
  margin: auto;
  padding: ${SpacingSize.XL}px;
  display: none;
  position: fixed;
  top: ${SpacingSize["3XL"]}px;
  left: 0;
  right: 0;
  z-index: 100;
  background-color: ${Color.White};
  border-radius: ${RadiusSize.Medium}px;
  ${Shadow.Deep};
  ${({ open }: StyledProps) => css`
    ${open &&
    css`
      display: block;
    `}
  `}
`

const StyledDialogOverlay = styled.div`
  position: fixed;
  z-index: 99;
  top: -25%;
  left: 0;
  bottom: 0;
  right: 0;
  height: 125%;
  width: 100%;
  background: #000;
  display: none;
  opacity: 0.6;
  ${({ open }: StyledProps) => css`
    ${open &&
    css`
      display: block;
    `}
  `}
`

const StyledDialogHeading = styled.div`
  margin-bottom: ${SpacingSize["3XS"]}px;
  font-size: ${TextSize.L}px;
  font-weight: bold;
  line-height: ${LineHeight.S}%;
  .sp-hide {
    ${IsMobile(css`
      display: none;
    `)}
  }
  .strong {
    color: ${Color.Attention};
  }
`

const StyledDialogContent = styled.div`
  margin-bottom: ${SpacingSize.M}px;
  font-size: ${TextSize.S}px;
  line-height: ${LineHeight.M}%;
`

const StyledDescription = styled.div`
  margin-bottom: ${SpacingSize.M}px;
  font-size: ${TextSize.S}px;
  line-height: ${LineHeight.M}%;
  color: ${Color.Gray};
`

const StyledSchduleWrapper = styled.div`
  overflow: scroll;
  -webkit-overflow-scrolling: touch;
  max-height: calc(100vh - 374px);
  ul {
    width: 100%;
  }
  .checkbox {
    &:not(:first-child) {
      margin-top: ${SpacingSize.XS}px;
    }
  }
`

const StyledActionPairButtonWrapper = styled.div`
  left: calc(50% - 140px);
  width: 100%;
  max-width: 280px;
  margin: auto;
  .close {
    text-align: center;
    padding: ${SpacingSize.M}px 0;
  }
`

const Label = styled.label`
  width: 100%;
  display: block;
  .checkbox-input {
    display: none;
    &:checked {
      & + .checkbox-content {
        background: ${Color.BackgroundGray};
        border: 1px solid ${Color.Primary};
        .checkbox-content-parts {
          background: ${Color.Primary};
          border: 1px solid ${Color.Primary};
          &:after {
            content: "";
            display: block;
            position: absolute;
            top: 1px;
            left: 5px;
            width: 8px;
            height: 12px;
            transform: rotate(40deg);
            border-bottom: 2px solid ${Color.White};
            border-right: 2px solid ${Color.White};
          }
        }
      }
    }
  }
  .checkbox-content {
    display: flex;
    align-items: center;
    width: 100%;
    padding: ${SpacingSize.S}px;
    border: 1px solid ${Color.BorderGray};
    border-radius: 2px;
    cursor: pointer;
    ${Transition.Hover};
    &:hover {
      border: 1px solid ${Color.Gray};
      .checkbox-content-parts {
        border: 1px solid ${Color.Gray};
      }
    }
  }
  .checkbox-content-parts {
    position: relative;
    display: block;
    width: 20px;
    height: 20px;
    border: 1px solid ${Color.LightGray};
    border-radius: 2px;
    ${Transition.Hover};
  }
  .checkbox-content-text {
    color: ${Color.Black};
    font-size: ${TextSize.M}px;
    margin-left: ${SpacingSize.XS}px;
  }
`

export default CourseSchduleSelectModal
